/**
 * AlbaranPriceAlertsBanner — Alert banner when a vendor invoice overcharges
 * for products vs the linked albarán. Fired by the backend when an albarán
 * is linked to an invoice and the invoice unit price exceeds the albarán
 * average by more than the tenant's `price_alert_threshold` (default 5%).
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronDown, ChevronUp, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function AlbaranPriceAlertsBanner({ lang = "es", formatMoney }) {
  const [alerts, setAlerts] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/alerts/price-discrepancies", {
        params: { only_unseen: 1 },
      });
      setAlerts(data.alerts || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!alerts.length) return null;

  const total = alerts.reduce((s, a) => s + Number(a.total_overpaid || 0), 0);

  const dismiss = async (a) => {
    try {
      await api.delete(`/alerts/price-discrepancies/${a.id}`);
      setAlerts((cur) => cur.filter((x) => x.id !== a.id));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const markSeen = async (a) => {
    try {
      await api.post(`/alerts/price-discrepancies/${a.id}/mark-seen`);
      setAlerts((cur) => cur.filter((x) => x.id !== a.id));
    } catch { /* silent */ }
  };

  const goToInvoice = (a) => {
    markSeen(a);
    navigate(`/invoices?id=${a.invoice_id}`);
  };

  return (
    <div
      className="rounded-xl border-2 border-red-300 bg-gradient-to-r from-red-50 via-red-100/50 to-red-50 px-5 py-4 shadow-sm"
      data-testid="albaran-price-alerts-banner"
      role="alert"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <AlertTriangle size={22} strokeWidth={2} className="text-red-800 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-red-600 text-white">
              {lang === "es" ? "Sobrecoste" : "Overcharge"}
            </span>
            <h3 className="text-base font-semibold text-red-900">
              {lang === "es"
                ? `${alerts.length} factura${alerts.length > 1 ? "s" : ""} cobra${alerts.length > 1 ? "n" : ""} más que su albarán`
                : `${alerts.length} invoice${alerts.length > 1 ? "s" : ""} charge more than their delivery note`}
            </h3>
          </div>
          <p className="text-[13px] text-[var(--text-secondary)] mt-1 leading-snug">
            {lang === "es"
              ? `Total sobrecoste detectado: `
              : `Total overcharge detected: `}
            <strong className="text-red-900 font-mono-num">
              {formatMoney ? formatMoney(total, alerts[0]?.currency || "EUR") : total.toFixed(2)}
            </strong>
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="h-9 px-3 text-xs border-red-300 text-red-800 hover:bg-red-50"
          onClick={() => setExpanded((v) => !v)}
          data-testid="albaran-price-alerts-toggle"
        >
          {expanded ? <ChevronUp size={13} className="mr-1" /> : <ChevronDown size={13} className="mr-1" />}
          {expanded
            ? (lang === "es" ? "Ocultar" : "Hide")
            : (lang === "es" ? "Ver detalles" : "See details")}
        </Button>
      </div>

      {expanded && (
        <div className="mt-4 space-y-2">
          {alerts.map((a) => (
            <div
              key={a.id}
              className="bg-white border border-red-200 rounded-lg p-3"
              data-testid={`albaran-alert-${a.id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[var(--text-primary)] truncate">
                    {a.vendor || "—"}
                    {a.invoice_number && <span className="text-[var(--text-muted)] font-normal ml-2">nº {a.invoice_number}</span>}
                    <span className="text-[var(--text-muted)] font-normal ml-2 font-mono-num text-xs">{a.invoice_date || ""}</span>
                  </p>
                  <p className="text-xs text-[var(--text-muted)] mt-0.5">
                    {(a.rows || []).length} {lang === "es" ? "líneas afectadas" : "affected lines"}
                    {" · "}
                    <strong className="text-red-800 font-mono-num">
                      +{formatMoney ? formatMoney(a.total_overpaid, a.currency) : Number(a.total_overpaid).toFixed(2)}
                    </strong>
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button
                    size="sm"
                    className="h-8 px-3 text-xs bg-red-600 hover:bg-red-700 text-white"
                    onClick={() => goToInvoice(a)}
                    data-testid={`albaran-alert-open-${a.id}`}
                  >
                    {lang === "es" ? "Ver factura" : "View invoice"}
                  </Button>
                  <button
                    onClick={() => dismiss(a)}
                    className="text-[var(--text-muted)] hover:text-[var(--text-primary)] p-1.5"
                    title={lang === "es" ? "Descartar" : "Dismiss"}
                    data-testid={`albaran-alert-dismiss-${a.id}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
              <div className="mt-2 border-t border-red-100 pt-2">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                      <th className="text-left py-1 font-medium">{lang === "es" ? "Producto" : "Product"}</th>
                      <th className="text-right py-1 font-medium">{lang === "es" ? "Cant." : "Qty"}</th>
                      <th className="text-right py-1 font-medium">{lang === "es" ? "Alb." : "Note"}</th>
                      <th className="text-right py-1 font-medium">{lang === "es" ? "Fact." : "Invoice"}</th>
                      <th className="text-right py-1 font-medium">%</th>
                      <th className="text-right py-1 font-medium">{lang === "es" ? "+" : "+"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(a.rows || []).map((r, i) => (
                      <tr key={`${r.product || "row"}-${i}`} className="border-t border-red-100/40">
                        <td className="py-1 truncate max-w-[220px]">{r.product}</td>
                        <td className="py-1 text-right font-mono-num">{r.qty}</td>
                        <td className="py-1 text-right font-mono-num text-[var(--text-muted)]">{Number(r.albaran_unit_price).toFixed(4)}</td>
                        <td className="py-1 text-right font-mono-num">{Number(r.invoice_unit_price).toFixed(4)}</td>
                        <td className="py-1 text-right font-mono-num text-red-700">+{Number(r.pct).toFixed(1)}%</td>
                        <td className="py-1 text-right font-mono-num font-semibold text-red-800">
                          +{formatMoney ? formatMoney(r.diff, a.currency) : Number(r.diff).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
